import { activitySummarySchema, type ProgramFull, type ProgressUpdate } from '@mio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../client';

import { homeSummaryQuery } from './home';
import { programKeys } from './program';

/**
 * Marks an activity started or complete. The program cache is updated optimistically so
 * the UI responds instantly; the server confirms in the background.
 */
export function useUpdateProgress(activityId: string) {
  const queryClient = useQueryClient();
  const key = programKeys.full();
  return useMutation({
    mutationFn: (update: ProgressUpdate) =>
      apiRequest(`/program/activities/${activityId}/progress`, activitySummarySchema, {
        method: 'PUT',
        body: update,
      }),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProgramFull>(key);
      if (previous && previous.progress[activityId] !== 'complete') {
        queryClient.setQueryData<ProgramFull>(key, {
          ...previous,
          progress: { ...previous.progress, [activityId]: update.status },
        });
      }
      return { previous };
    },
    onError: (_error, _update, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: homeSummaryQuery.queryKey });
    },
  });
}
